import type {CaptureDefaultValues} from 'modules/VendorRegister/types';

import {PlayCircleOutlined} from '@ant-design/icons';
import {Button, Loading, Modal} from '@ds/components';
import {toast} from '@Toast';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useAppDispatch} from 'store';
import {uploadFile} from 'store/slices/vendor-registration/thunk';

const RECORD_DURATION_SECONDS = 15;

type RecordingPhase = 'camera' | 'recording' | 'preview';

type VideoRecordModalProps = {
  show: boolean;
  onClose: () => void;
  onRecordingComplete: (id: string | number, file: File) => void;
  defaultValues: CaptureDefaultValues;
};

const VideoRecordModal: React.FC<VideoRecordModalProps> = ({
  show,
  onClose,
  onRecordingComplete,
  defaultValues
}) => {
  const dispatch = useAppDispatch();
  const liveVideoRef = useRef<HTMLVideoElement>(null);
  const previewVideoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const processedStreamRef = useRef<MediaStream | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const recordedUrlRef = useRef<string | null>(null);
  const discardRecordingRef = useRef(false);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(true);
  const [phase, setPhase] = useState<RecordingPhase>('camera');
  const [recordedUrl, setRecordedUrl] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);

  const setPreviewUrl = (url: string | null) => {
    if (recordedUrlRef.current) {
      URL.revokeObjectURL(recordedUrlRef.current);
    }

    recordedUrlRef.current = url;
    setRecordedUrl(url);
  };

  const stopDrawing = useCallback(() => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, []);

  const closeCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    processedStreamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    processedStreamRef.current = null;
    stopDrawing();
  }, [stopDrawing]);

  const openCamera = async () => {
    setCameraLoading(true);
    const mediaStream = await navigator.mediaDevices.getUserMedia({
      video: {facingMode: 'user'},
      audio: false
    });
    streamRef.current = mediaStream;

    if (liveVideoRef.current) {
      liveVideoRef.current.srcObject = mediaStream;
    }
  };

  const clearRecordingTimer = () => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const resetState = () => {
    clearRecordingTimer();
    discardRecordingRef.current = true;

    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }

    mediaRecorderRef.current = null;
    closeCamera();
    setPreviewUrl(null);
    setCameraLoading(true);
    setPhase('camera');
    setPlaying(false);
    setVideoFile(null);
  };

  useEffect(() => {
    if (!show) {
      return;
    }

    let cancelled = false;

    if (defaultValues.file) {
      setPreviewUrl(URL.createObjectURL(defaultValues.file));
      setVideoFile(defaultValues.file);
      setPhase('preview');
      setCameraLoading(false);
    } else {
      void openCamera().catch(() => {
        if (!cancelled) {
          setCameraLoading(false);
          toast('برای ضبط ویدیو، دسترسی به دوربین را مجاز کنید');
        }
      });
    }

    return () => {
      cancelled = true;
      clearRecordingTimer();
      discardRecordingRef.current = true;
      if (mediaRecorderRef.current?.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      mediaRecorderRef.current = null;
      closeCamera();
      if (recordedUrlRef.current) {
        URL.revokeObjectURL(recordedUrlRef.current);
        recordedUrlRef.current = null;
      }
    };
  }, [closeCamera, defaultValues.file, show]);

  const handleStartRecording = () => {
    const sourceVideo = liveVideoRef.current;

    if (!streamRef.current || !sourceVideo) {
      return;
    }

    try {
      const canvas = document.createElement('canvas');
      canvas.width = sourceVideo.videoWidth || 640;
      canvas.height = sourceVideo.videoHeight || 480;
      const context = canvas.getContext('2d');

      if (!context) {
        toast('امکان شروع ضبط ویدیو وجود ندارد');
        return;
      }

      const processedStream = canvas.captureStream(30);
      processedStreamRef.current = processedStream;
      const supportedMimeType = ['video/mp4', 'video/webm;codecs=vp9', 'video/webm'].find((type) =>
        MediaRecorder.isTypeSupported(type)
      );
      const recorder = new MediaRecorder(processedStream, {
        ...(supportedMimeType ? {mimeType: supportedMimeType} : {}),
        videoBitsPerSecond: 800_000
      });

      const drawFrame = () => {
        if (mediaRecorderRef.current?.state !== 'recording') {
          return;
        }

        context.save();
        context.translate(canvas.width, 0);
        context.scale(-1, 1);
        context.drawImage(sourceVideo, 0, 0, canvas.width, canvas.height);
        context.restore();
        animationFrameRef.current = requestAnimationFrame(drawFrame);
      };

      chunksRef.current = [];
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };
      recorder.onstop = () => {
        clearRecordingTimer();
        closeCamera();

        if (discardRecordingRef.current) {
          chunksRef.current = [];
          return;
        }

        const mimeType = supportedMimeType?.split(';')[0] ?? 'video/webm';
        const extension = mimeType === 'video/mp4' ? 'mp4' : 'webm';
        const blob = new Blob(chunksRef.current, {type: mimeType});
        const file = new File([blob], `video-recording.${extension}`, {type: mimeType});
        setPreviewUrl(URL.createObjectURL(blob));
        setVideoFile(file);
        setPhase('preview');
      };

      mediaRecorderRef.current = recorder;
      discardRecordingRef.current = false;
      recorder.start();
      setPhase('recording');
      animationFrameRef.current = requestAnimationFrame(drawFrame);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        if (recorder.state !== 'inactive') {
          recorder.stop();
        }
      }, RECORD_DURATION_SECONDS * 1000);
    } catch {
      closeCamera();
      setCameraLoading(false);
      toast('مرورگر شما امکان ضبط ویدیو را پشتیبانی نمی‌کند');
    }
  };

  const handleStopRecording = () => {
    clearRecordingTimer();
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
  };

  const handlePlayPreview = () => {
    void previewVideoRef.current?.play();
    setPlaying(true);
  };

  const handleReRecord = () => {
    setPreviewUrl(null);
    setPlaying(false);
    setCameraLoading(true);
    setPhase('camera');
    void openCamera().catch(() => {
      setCameraLoading(false);
      toast('برای ضبط ویدیو، دسترسی به دوربین را مجاز کنید');
    });
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleUpload = async () => {
    if (!videoFile) {
      return;
    }

    const formData = new FormData();
    formData.append('file', videoFile);
    setUploading(true);

    try {
      const response = await dispatch(uploadFile(formData)).unwrap();

      if (response.isSuccess) {
        onRecordingComplete(response.data.id, videoFile);
        handleClose();
      } else {
        toast(response.message || 'خطا');
      }
    } catch (error) {
      toast(error instanceof Error ? error.message : 'خطا');
    } finally {
      setUploading(false);
    }
  };

  const showLiveVideo = phase === 'camera' || phase === 'recording';

  return (
    <Modal
      show={show}
      onClose={handleClose}
      canceledOnTouchOutside={phase !== 'recording'}
      defaultHeader
      title="ضبط ویدیو"
    >
      <div className="flex flex-col items-center gap-3 p-4">
        <div className="relative flex size-49 items-center justify-center">
          {phase === 'recording' ? (
            <svg
              className="absolute inset-0 size-full -rotate-90"
              viewBox="0 0 200 200"
              aria-hidden
            >
              <circle
                cx="100"
                cy="100"
                r="96"
                fill="none"
                stroke="currentColor"
                strokeWidth="4"
                className="text-gray-04"
              />
              <circle
                cx="100"
                cy="100"
                r="96"
                fill="none"
                stroke="currentColor"
                strokeWidth="4"
                strokeLinecap="round"
                strokeDasharray={1}
                strokeDashoffset={1}
                pathLength="1"
                className="text-orange"
              >
                <animate
                  attributeName="stroke-dashoffset"
                  from="1"
                  to="0"
                  dur="15s"
                  fill="freeze"
                />
              </circle>
            </svg>
          ) : null}
          <div className="relative flex size-45 items-center justify-center rounded-full border border-gray-03 bg-gray-03">
            <video
              ref={liveVideoRef}
              autoPlay
              muted
              playsInline
              className={`size-full scale-x-[-1] rounded-full object-cover ${
                showLiveVideo ? 'block' : 'hidden'
              }`}
              onPlaying={() => setCameraLoading(false)}
            />
            {phase === 'preview' && recordedUrl ? (
              <video
                ref={previewVideoRef}
                src={recordedUrl}
                playsInline
                className="size-full rounded-full object-cover"
                onEnded={() => setPlaying(false)}
              />
            ) : null}
            {cameraLoading && phase === 'camera' ? (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loading />
              </div>
            ) : null}
            {phase === 'preview' && !playing ? (
              <button
                type="button"
                className="absolute inset-0 flex cursor-pointer items-center justify-center rounded-full bg-black/20 text-white"
                onClick={handlePlayPreview}
                aria-label="پخش ویدیوی ضبط‌شده"
              >
                <PlayCircleOutlined aria-hidden className="text-heading-h1" />
              </button>
            ) : null}
          </div>
        </div>
        <div className="flex flex-col items-center gap-3">
          <p className="text-center text-body-sm text-gray-09">
            لطفاً از صورت خود ویدیویی واضح ضبط کنید و سر را به چپ، راست، بالا و پایین حرکت دهید.
          </p>
          {phase === 'camera' ? (
            <Button size="md" onClick={handleStartRecording} disabled={cameraLoading}>
              ضبط ویدیو
            </Button>
          ) : null}
          {phase === 'recording' ? (
            <Button size="md" onClick={handleStopRecording}>
              توقف ضبط
              <span className="size-3 animate-pulse rounded-full bg-destructive" aria-hidden />
            </Button>
          ) : null}
          {phase === 'preview' ? (
            <>
              <Button size="md" variant="fill" color="secondary" onClick={handleReRecord}>
                ضبط مجدد
              </Button>
              <Button size="md" onClick={handleUpload} loading={uploading}>
                تایید و ارسال
              </Button>
            </>
          ) : null}
        </div>
      </div>
    </Modal>
  );
};

export default VideoRecordModal;
